const CALM_PICK_SIZE = 5;
const CALM_LOOKBACK_DAYS = 90;
const SEASON_PICK_SIZE = 5;
const SEASON_LOOKBACK_DAYS = 14;
export const ALT_MIN_BREADTH = 0.6;
export const ALT_MIN_MED30 = 0.1;
/** `close_BTC(i) > SMA100_BTC(i)`; `false` (never an error) when SMA100 is not yet defined. */
export function btcOn(ctx, i) {
    const j = ctx.panel.btcIndex;
    const close = ctx.panel.close[j]?.[i] ?? null;
    const sma = ctx.indicators.sma100[j]?.[i] ?? null;
    return close !== null && sma !== null && close > sma;
}
/** `breadth(i) >= 0.6 AND med30(i) >= 0.10 AND btcOn(i)`. */
export function isAltSeason(ctx, i) {
    const breadth = ctx.indicators.breadth[i] ?? null;
    const med30 = ctx.indicators.med30[i] ?? null;
    return (breadth !== null &&
        breadth >= ALT_MIN_BREADTH &&
        med30 !== null &&
        med30 >= ALT_MIN_MED30 &&
        btcOn(ctx, i));
}
/**
 * Ascending sort of `candidates` by `key(candidate)`, ties broken by `tie(candidate)` ascending.
 * The Python oracle ranks with `np.argsort` (introsort), whose tie order is an implementation
 * artifact of the input array layout, not a strategy rule; no tie is ever observed on the real
 * Bybit panel (confirmed by the parity fixture), so reproducing NumPy's unstable order is not
 * attempted. Symbol-name-ascending gives a tie-break that is deterministic and independent of
 * coin-index/input order, which an index-based tie-break would not be.
 */
function stableSortAsc(candidates, key, tie) {
    return candidates
        .map((value) => ({ value, key: key(value), tie: tie(value) }))
        .sort((a, b) => a.key - b.key || (a.tie < b.tie ? -1 : a.tie > b.tie ? 1 : 0))
        .map((entry) => entry.value);
}
/** Descending counterpart of `stableSortAsc` (same tie-break rule; see its docstring). */
function stableSortDesc(candidates, key, tie) {
    return candidates
        .map((value) => ({ value, key: key(value), tie: tie(value) }))
        .sort((a, b) => b.key - a.key || (a.tie < b.tie ? -1 : a.tie > b.tie ? 1 : 0))
        .map((entry) => entry.value);
}
/**
 * `U(i)` coins with a close 90 days ago and a defined σ90, ascending σ90 (ties broken by symbol
 * name ascending, see `stableSortAsc`), first 5; empty when fewer than 5 qualify (merged_run.py
 * `lowvol_phase`'s `F17._rank(..., 5, 90, asc=True)`).
 */
export function calmPick(ctx, i) {
    if (i < CALM_LOOKBACK_DAYS)
        return [];
    const coins = ctx.universe.universe[i] ?? [];
    const qualifying = coins.filter((j) => {
        const pastClose = ctx.panel.close[j]?.[i - CALM_LOOKBACK_DAYS] ?? null;
        const sigma = ctx.indicators.sigma90[j]?.[i] ?? null;
        return pastClose !== null && sigma !== null;
    });
    if (qualifying.length < CALM_PICK_SIZE)
        return [];
    return stableSortAsc(qualifying, (j) => ctx.indicators.sigma90[j]?.[i] ?? 0, (j) => ctx.panel.bases[j] ?? "").slice(0, CALM_PICK_SIZE);
}
/**
 * `U(i)` coins with a close 14 days ago, descending `close(i)/close(i-14)` (ties broken by symbol
 * name ascending, see `stableSortDesc`), first 5; empty when fewer than 5 qualify (spot15/families.py
 * `_top_by_return`, called with `look=14, k=5`).
 */
export function seasonPick(ctx, i) {
    if (i < SEASON_LOOKBACK_DAYS)
        return [];
    const coins = ctx.universe.universe[i] ?? [];
    const qualifying = coins.filter((j) => (ctx.panel.close[j]?.[i - SEASON_LOOKBACK_DAYS] ?? null) !== null);
    if (qualifying.length < SEASON_PICK_SIZE)
        return [];
    return stableSortDesc(qualifying, (j) => {
        const close = ctx.panel.close[j]?.[i] ?? 0;
        const past = ctx.panel.close[j]?.[i - SEASON_LOOKBACK_DAYS] ?? 1;
        return close / past;
    }, (j) => ctx.panel.bases[j] ?? "").slice(0, SEASON_PICK_SIZE);
}
